import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { ArrowLeft, CheckCircle, AlertCircle, Lock } from 'lucide-react';
import { auth } from '../firebase/config';
import LanguageSelector from '../components/LanguageSelector';

const ResetPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');

  useEffect(() => {
    let mounted = true;

    const validateCode = async () => {
      if (!oobCode || mode !== 'resetPassword') {
        if (mounted) {
          setError(t('resetPassword.invalidLink'));
          setLoading(false);
        }
        return;
      }

      try {
        const targetEmail = await verifyPasswordResetCode(auth, oobCode);
        if (!mounted) return;
        setEmail(targetEmail);
        setLoading(false);
      } catch (e: any) {
        if (!mounted) return;
        if (e?.code === 'auth/expired-action-code' || e?.code === 'auth/invalid-action-code') {
          setError(t('resetPassword.invalidLink'));
        } else {
          setError(t('resetPassword.invalidLink'));
        }
        setLoading(false);
      }
    };

    validateCode();
    return () => {
      mounted = false;
    };
  }, [mode, oobCode, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!oobCode) return;

    if (!password || !confirmPassword) {
      setError(t('resetPassword.invalidLink'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('resetPassword.passwordMismatch'));
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await confirmPasswordReset(auth, oobCode, password);
      setDone(true);
    } catch (e: any) {
      if (e?.code === 'auth/weak-password') {
        setError(t('resetPassword.weakPassword'));
      } else if (e?.code === 'auth/expired-action-code' || e?.code === 'auth/invalid-action-code') {
        setError(t('resetPassword.invalidLink'));
      } else {
        setError(t('resetPassword.invalidLink'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="absolute top-4 right-4">
          <LanguageSelector variant="button" />
        </div>
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="text-center">
              <CheckCircle className="mx-auto h-12 w-12 text-green-500 mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('resetPassword.successTitle')}</h2>
              <p className="text-gray-600 mb-6">{t('resetPassword.successMessage')}</p>
              <button
                onClick={() => navigate('/login')}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                {t('auth.signIn')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="absolute top-4 right-4">
        <LanguageSelector variant="button" />
      </div>
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="mb-6">
            <Link to="/login" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700">
              <ArrowLeft className="h-4 w-4 mr-1" />
              {t('resetPassword.backToLogin')}
            </Link>
          </div>

          <div className="text-center mb-6">
            <Lock className="mx-auto h-12 w-12 text-primary-600 mb-4" />
            <h2 className="text-2xl font-bold text-gray-900">{t('resetPassword.title')}</h2>
            {!loading && !error && (
              <p className="mt-2 text-sm text-gray-600">{t('resetPassword.subtitle', { email })}</p>
            )}
          </div>

          {loading ? (
            <div className="text-center text-sm text-gray-600">{t('resetPassword.saving')}</div>
          ) : (
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  {t('resetPassword.passwordLabel')}
                </label>
                <div className="mt-1">
                  <input
                    id="password"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder={t('resetPassword.passwordPlaceholder')}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                  {t('resetPassword.confirmPasswordLabel')}
                </label>
                <div className="mt-1">
                  <input
                    id="confirmPassword"
                    type="password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder={t('resetPassword.confirmPasswordPlaceholder')}
                  />
                </div>
              </div>

              {error && (
                <div className="rounded-md bg-slate-50 p-4">
                  <div className="flex">
                    <AlertCircle className="h-5 w-5 text-slate-400" />
                    <div className="ml-3">
                      <p className="text-sm text-slate-700">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || loading || Boolean(error && !password)}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? t('resetPassword.saving') : t('resetPassword.saveButton')}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
